import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { FoodOrder, FoodOrderStateMerged, ORDER_STATUS } from './data.d';
import { api, ApiResponse, authHeader } from '@you-go-vn/components';

const LOCALSTORAGE_NAME = 'processingOrders';
const DONE_STATUS = [
  ORDER_STATUS.CANCELLED,
  ORDER_STATUS.COMPLETED,
  ORDER_STATUS.PICKED,
  ORDER_STATUS.SHIPPER_OK,
];
export type ProcessingOrders = FoodOrderStateMerged[];

const genDefaultApi = (restaurantId: number, path: string) =>
  '/api/orders/' + restaurantId + path;

type State = {
  isLoading: boolean;
  processingOrders: ProcessingOrders;
  doneOrders: FoodOrderStateMerged[];
};

const initialState: State = {
  isLoading: true,
  processingOrders: [],
  doneOrders: [],
};

type Params = {
  restaurantId: number;
};
export const fetchProcessingOrders = createAsyncThunk(
  LOCALSTORAGE_NAME,
  async ({ restaurantId }: Params, thunkAPI) => {
    try {
      // const res = await ordersService.getOrdersProcessingByRestaurantId(restaurantId);
      const res = await api.get<ApiResponse<FoodOrder[]>>(
        genDefaultApi(restaurantId, '/processing'),
        {
          headers: authHeader(),
        },
      );

      let processingOrders: ProcessingOrders = initialState.processingOrders;
      let doneOrders: FoodOrderStateMerged[] = initialState.doneOrders;
      const fetchedProcessingOrders = res.data.data;
      const lsProcessingOrders = localStorage.getItem(LOCALSTORAGE_NAME);
      const cachedProcessingOrders =
        typeof lsProcessingOrders === 'string'
          ? (JSON.parse(lsProcessingOrders) as Array<FoodOrderStateMerged>)
          : undefined;
      if (!cachedProcessingOrders) {
        processingOrders = fetchedProcessingOrders
          .filter((x) => !DONE_STATUS.includes(x.status))
          .map((x) => {
            return {
              ...x,
              viewedDetail: false,
            };
          });
      } else {
        processingOrders = fetchedProcessingOrders.map((x) => {
          const xC = cachedProcessingOrders.find((xC) => xC.id === x.id);
          if (xC) {
            return {
              ...x,
              viewedDetail: xC.viewedDetail,
            };
          } else {
            return {
              ...x,
              viewedDetail: false,
            };
          }
        });
      }
      doneOrders = fetchedProcessingOrders
        .filter((x) => DONE_STATUS.includes(x.status))
        .map((x) => {
          return {
            ...x,
            viewedDetail: true,
          };
        });
      return {
        processingOrders,
        doneOrders,
      };
    } catch (error: any) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  },
);

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    updateViewedDetail: (state, action: PayloadAction<number>) => {
      state.processingOrders = state.processingOrders.map((pO) => {
        if (pO.id === action.payload) {
          pO.viewedDetail = true;
        }
        return pO;
      });
      localStorage.setItem(
        LOCALSTORAGE_NAME,
        JSON.stringify(state.processingOrders),
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchProcessingOrders.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchProcessingOrders.fulfilled, (state, action) => {
      state.isLoading = false;
      state.processingOrders = action.payload.processingOrders;
      state.doneOrders = action.payload.doneOrders;
      localStorage.setItem(
        LOCALSTORAGE_NAME,
        JSON.stringify(state.processingOrders),
      );
    });
    builder.addCase(fetchProcessingOrders.rejected, (state) => {
      state.isLoading = false;
      state.processingOrders = initialState.processingOrders;
      state.doneOrders = initialState.doneOrders;
      localStorage.setItem(
        LOCALSTORAGE_NAME,
        JSON.stringify(initialState.processingOrders),
      );
    });
  },
});

export default orderSlice;
